import React, { Dispatch, SetStateAction } from "react"
import { CustomReasonCode, InputReason } from "lib/types/generated/graphql-types"
import Input from "../../../Ui/Form/Input"
import {
  ActionButtonWrapper,
  Button,
  ButtonWrapper,
  Container,
  Info,
  InputWrapper,
  StyledInput,
  StyledSelect
} from "./ReasonInput.styled"
import { Link } from "react-router-dom"
import { ReactComponent as InfoIcon } from "../../../../images/icons/circle-info.svg"
import { ReactComponent as TagIcon } from "../../../../images/icons/tag.svg"
import { ReactComponent as PenIcon } from "../../../../images/icons/pen-field.svg"
import { ReactComponent as MinusIcon } from "../../../../images/icons/circle-minus.svg"
import { ReasonType } from "lib/types/order"
import PrimaryButton from "../../../Ui/Buttons/PrimaryButton"
import SecondaryButton from "../../../Ui/Buttons/SecondaryButton"
import { isSkipReason } from "../../helpers"

type Props = {
  setReason: Dispatch<SetStateAction<InputReason>>
  reason: InputReason
  addReason?: () => void
  addReasonLoading?: boolean
  customReasons: CustomReasonCode[]
  reasonType: ReasonType | undefined
  setReasonType: Dispatch<SetStateAction<ReasonType | undefined>>
  hideSkipType?: boolean
}

const ReasonInput = ({
  setReason,
  reason,
  addReason,
  addReasonLoading,
  customReasons,
  reasonType,
  setReasonType
}: Props) => {
  const getOptions = () => {
    return customReasons?.map((reason: CustomReasonCode) => ({
      label: `${reason.label} - ${reason.code}`,
      value: reason.code
    }))
  }

  const selectedOption = (code: string) =>
    getOptions()?.find((option: { value: string; label: string }) => option.value === code)

  const updateCode = (value: string) => setReason((prev) => ({ ...prev, code: value }))
  const updateCause = (value: string) => setReason((prev) => ({ ...prev, cause: value }))

  const changeReasonType = (selectedType: ReasonType) => {
    if (reasonType !== selectedType) setReason({ code: "", cause: "" })
    setReasonType(selectedType)
  }

  return (
    <Container>
      <ButtonWrapper>
        <Button
          $selected={reasonType === ReasonType.PRE_DEFINED}
          onClick={() => changeReasonType(ReasonType.PRE_DEFINED)}
        >
          <b>
            <TagIcon />
            Pre-defined
          </b>
          <span>Pick from list</span>
        </Button>
        <Button
          $selected={reasonType === ReasonType.CUSTOM_INPUT}
          onClick={() => changeReasonType(ReasonType.CUSTOM_INPUT)}
        >
          <b>
            <PenIcon />
            Custom
          </b>
          <span>Enter a custom reason</span>
        </Button>
        <Button
          $selected={reasonType === ReasonType.SKIP}
          onClick={() => changeReasonType(ReasonType.SKIP)}
        >
          <b>
            <MinusIcon />
            No reason
          </b>
          <span>Continue without a reason </span>
        </Button>
      </ButtonWrapper>

      {reasonType === ReasonType.PRE_DEFINED && (
        <Info>
          <InfoIcon />
          Pre-defined reason codes handled in
          <Link to={"/settings/order-definitions"}> Policy definitions</Link>
        </Info>
      )}
      {reasonType === ReasonType.PRE_DEFINED && (
        <>
          <StyledSelect
            label={"Code"}
            options={getOptions()}
            setValue={updateCode}
            value={selectedOption(reason?.code ?? "")}
          />
          <Input
            label={"Comment"}
            defaultValue={reason?.cause ?? ""}
            name={"cause"}
            placeholder={"Comment"}
            onChange={(e: React.FormEvent<HTMLInputElement>) => updateCause(e.currentTarget.value)}
          />
        </>
      )}
      <InputWrapper>
        {reasonType === ReasonType.CUSTOM_INPUT && (
          <>
            <Info>
              <InfoIcon />
              Input reason code with text
            </Info>
            <StyledInput
              label={"Code"}
              defaultValue={reason?.code ?? ""}
              name={"code"}
              placeholder={"Code"}
              onChange={(e: React.FormEvent<HTMLInputElement>) => updateCode(e.currentTarget.value)}
            />
            <StyledInput
              label={"Comment"}
              defaultValue={reason?.cause ?? ""}
              name={"cause"}
              placeholder={"Comment"}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                updateCause(e.currentTarget.value)
              }
            />
          </>
        )}
        {reasonType === ReasonType.SKIP && (
          <Info>
            <InfoIcon />
            No reason will be added
          </Info>
        )}
        {reasonType === undefined && (
          <Info>
            <InfoIcon />
            Select how you want to handle the reason
          </Info>
        )}
        {addReason && !isSkipReason(reasonType) && !!ReasonType && (
          <ActionButtonWrapper>
            <PrimaryButton
              disabled={!reason.cause || !reason.code || addReasonLoading}
              loading={addReasonLoading}
              handleClick={addReason}
            >
              Save
            </PrimaryButton>
            <SecondaryButton handleClick={() => setReasonType(ReasonType.SKIP)}>
              Cancel
            </SecondaryButton>
          </ActionButtonWrapper>
        )}
      </InputWrapper>
    </Container>
  )
}

export default ReasonInput
